// src/components/ActionBoardContent/ActionAreaAccordion.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Button, Spinner, Collapse } from 'react-bootstrap';
import { IconChevronDown, IconChevronUp, IconPencil } from '@tabler/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import ActionAreaOffcanvas from '../offcanvas/ActionAreaOffcanvas';
import MeasureOffcanvas from '../offcanvas/MeasureOffcanvas';
import { createMeasure, fetchMeasureById } from '../../features/measuresSlice';

import './ActionAreaAccordion.css';

/**
 * Status options for action area goal achievement (Bewertung Ergebnis)
 * These match the options in ActionAreaOffcanvas FormDropdown
 */
const STATUS_OPTIONS = [
    { value: '', label: 'Status wählen' },
    { value: 'mehr als erreicht', label: 'Mehr als erreicht' },
    { value: 'erreicht', label: 'Erreicht' },
    { value: 'nicht erreicht', label: 'Nicht erreicht' },
];

const getActionAreaStatusClass = (status) => {
    switch (status) {
        case 'nicht erreicht':
            return 'status-not-achieved';
        case 'erreicht':
            return 'status-achieved';
        case 'mehr als erreicht':
            return 'status-overachieved';
        default:
            return 'status-no-status';
    }
};

/**
 * ActionAreaAccordion Component
 * 
 * Displays an action area as an expandable accordion with:
 * - Title and edit button
 * - Measure count link
 * - Status dropdown (Bewertung Ergebnis)
 * - Add measure button
 * - Expandable measures list
 */
const ActionAreaAccordion = ({
    actionArea,
    onSaveActionArea,
    isReadOnly = false,
    unitName = '',
    shouldOpenOffcanvas = false,
    onOffcanvasClose,
}) => {
    const dispatch = useDispatch();

    // Local UI state
    const [isExpanded, setIsExpanded] = useState(false);
    const [showActionAreaOffcanvas, setShowActionAreaOffcanvas] = useState(false);
    const [openOffcanvasToResults, setOpenOffcanvasToResults] = useState(false);
    const [selectedMeasureId, setSelectedMeasureId] = useState(null);
    const [isCreatingMeasure, setIsCreatingMeasure] = useState(false);

    // Editable copy of action area for local changes
    const [editableActionArea, setEditableActionArea] = useState(actionArea);

    // Redux selectors
    const measureEntities = useSelector((state) => state.measures.entities);

    // Derived values
    const measureCount = useMemo(() => actionArea.measures?.length || 0, [actionArea.measures]);

    // Sync editable state when prop changes
    useEffect(() => {
        setEditableActionArea(actionArea);
    }, [actionArea]);

    // Handle auto-open offcanvas for newly created action areas
    useEffect(() => {
        if (shouldOpenOffcanvas) {
            setShowActionAreaOffcanvas(true);
        }
    }, [shouldOpenOffcanvas]);

    // Fetch measures that aren't in the store yet
    useEffect(() => {
        if (!actionArea.measures) return;

        actionArea.measures.forEach((measure) => {
            if (!measureEntities[measure.id]) {
                dispatch(fetchMeasureById({ actionAreaId: actionArea.id, measureId: measure.id }));
            }
        });
    }, [actionArea.measures, actionArea.id, measureEntities, dispatch]);

    // ===== Event Handlers =====

    const toggleAccordion = useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    const handleStatusChange = useCallback(
        (e) => {
            if (isReadOnly) return;

            const newStatus = e.target.value;
            const updatedActionArea = { ...editableActionArea, status: newStatus };

            setEditableActionArea(updatedActionArea);
            onSaveActionArea?.(updatedActionArea);
        },
        [isReadOnly, editableActionArea, onSaveActionArea]
    );

    const handleOpenActionAreaOffcanvas = useCallback((e, openToResults = false) => {
        e?.stopPropagation();
        setOpenOffcanvasToResults(openToResults);
        setShowActionAreaOffcanvas(true);
    }, []);

    const handleCloseActionAreaOffcanvas = useCallback(() => {
        setShowActionAreaOffcanvas(false);
        setOpenOffcanvasToResults(false);

        // Save changes if there were any modifications
        if (JSON.stringify(editableActionArea) !== JSON.stringify(actionArea)) {
            onSaveActionArea?.(editableActionArea);
        }

        onOffcanvasClose?.();
    }, [editableActionArea, actionArea, onSaveActionArea, onOffcanvasClose]);

    const handleActionAreaDataChange = useCallback((updates) => {
        setEditableActionArea((prev) => ({ ...prev, ...updates }));
    }, []);

    const handleOpenMeasureOffcanvas = useCallback((measureId, e) => {
        e?.stopPropagation();
        setSelectedMeasureId(measureId);
    }, []);

    const handleCloseMeasureOffcanvas = useCallback(() => {
        setSelectedMeasureId(null);
    }, []);

    const handleCreateMeasure = useCallback(
        async (e) => {
            e?.stopPropagation();
            if (isReadOnly || isCreatingMeasure) return;

            setIsCreatingMeasure(true);

            try {
                const created = await dispatch(
                    createMeasure({
                        actionAreaId: actionArea.id,
                        measure: { measure_title: '' },
                        unitId: actionArea.unit,
                    })
                ).unwrap();

                // Expand accordion and open new measure's offcanvas
                setIsExpanded(true);
                setSelectedMeasureId(created.measure.id);
            } catch (error) {
                console.error('Error creating measure:', error);
            } finally {
                setIsCreatingMeasure(false);
            }
        },
        [isReadOnly, isCreatingMeasure, actionArea.id, actionArea.unit, dispatch]
    );

    // ===== Helper Functions =====

    const getMeasureTitle = useCallback(
        (measureId) => {
            const measure = measureEntities[measureId];
            return measure?.measure_title || 'Neue Maßnahme';
        },
        [measureEntities]
    );

    const displayTitle = editableActionArea.actionarea_title || 'Unbenanntes Handlungsfeld';

    // ===== Render =====

    return (
        <div className="action-area-accordion">
            {/* Accordion Header */}
            <div className="accordion-header">
                {/* Left Section: Status indicator, title, edit button, measure count */}
                <div className="accordion-header-left">
                    <div className="accordion-title-row">
                        <div className={`status-indicator ${getActionAreaStatusClass(editableActionArea.status)}`} aria-hidden="true" />
                        <h3 className="accordion-title">{displayTitle}</h3>
                        <button
                            type="button"
                            className="edit-action-area-btn"
                            onClick={handleOpenActionAreaOffcanvas}
                            aria-label={`${displayTitle} bearbeiten`}
                        >
                            <IconPencil size={18} stroke={1.5} />
                        </button>
                    </div>

                    <button
                        type="button"
                        className="accordion-measure-count"
                        onClick={toggleAccordion}
                        aria-expanded={isExpanded}
                    >
                        Maßnahmen anzeigen ({measureCount})
                    </button>
                </div>

                {/* Right Section: Add Measure Button and Toggle */}
                <div className="accordion-header-right">
                    {!isReadOnly && (
                        <Button
                            type="button"
                            className="add-measure-header-btn me-2"
                            onClick={handleCreateMeasure}
                            disabled={isCreatingMeasure}
                        >
                            {isCreatingMeasure ? (
                                <Spinner size="sm" animation="border" role="status">
                                    <span className="visually-hidden">Wird erstellt...</span>
                                </Spinner>
                            ) : (
                                'Maßnahmen +'
                            )}
                        </Button>
                    )}
                    <button
                        type="button"
                        className="accordion-toggle-btn"
                        onClick={toggleAccordion}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Maßnahmen ausblenden' : 'Maßnahmen anzeigen'}
                    >
                        {isExpanded ? (
                            <IconChevronUp size={24} stroke={2} />
                        ) : (
                            <IconChevronDown size={24} stroke={2} />
                        )}
                    </button>
                </div>
            </div>

            {/* Accordion Content (Expanded) */}
            <Collapse in={isExpanded}>
                <div className="accordion-content">
                    <div className="accordion-content-inner">
                        {/* Measures Section Header */}
                        <div className="measures-section-header">
                            <div className="measures-section-info">
                                <p className="measures-section-description">
                                    Wurde das gesetzte Ziel in diesem Handlungsfeld erreicht?
                                    <br />
                                    <button
                                        type="button"
                                        className="results-link"
                                        onClick={(e) => handleOpenActionAreaOffcanvas(e, true)}
                                    >
                                        Finde die Ziele in deinem Handlungsfeld
                                    </button>
                                </p>
                            </div>
                            <div className="measures-section-status">
                                <div className="status-dropdown-wrapper">
                                    <Form.Select
                                        value={editableActionArea.status || ''}
                                        onChange={handleStatusChange}
                                        disabled={isReadOnly}
                                        className={`status-dropdown ${getActionAreaStatusClass(editableActionArea.status)}`}
                                        aria-label="Bewertung Ergebnis"
                                    >
                                        {STATUS_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                            </div>
                            <div className="measures-section-title-wrapper">
                                <h4 className="measures-section-title pt-3">Maßnahmen</h4>
                            </div>
                        </div>

                        {/* Measures List */}
                        <div className="measures-list-accordion">
                            {measureCount > 0 ? (
                                actionArea.measures.map((measure) => (
                                        <div key={measure.id} className="measure-row">
                                            <div className="measure-title-wrapper">
                                                <button
                                                    type="button"
                                                    className="measure-title-text"
                                                    onClick={(e) => handleOpenMeasureOffcanvas(measure.id, e)}
                                                    aria-label={`${getMeasureTitle(measure.id)} bearbeiten`}
                                                >
                                                    {getMeasureTitle(measure.id)}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="edit-measure-btn"
                                                    onClick={(e) => handleOpenMeasureOffcanvas(measure.id, e)}
                                                    aria-label={`${getMeasureTitle(measure.id)} bearbeiten`}
                                                >
                                                    <IconPencil size={18} stroke={1.5} />
                                                </button>
                                            </div>
                                        </div>
                                ))
                            ) : (
                                <div className="no-measures-message">
                                    Noch keine Maßnahmen hinzugefügt. Klicke auf "Maßnahmen +",
                                    um eine neue Maßnahme zu erstellen.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Collapse>

            {/* Offcanvas Components */}
            {showActionAreaOffcanvas && (
                <ActionAreaOffcanvas
                    showOffcanvas={showActionAreaOffcanvas}
                    handleCloseOffcanvas={handleCloseActionAreaOffcanvas}
                    actionAreaData={editableActionArea}
                    handleActionAreaDataChange={handleActionAreaDataChange}
                    unitName={unitName}
                    isUnitLeader={!isReadOnly}
                    openToResults={openOffcanvasToResults}
                />
            )}

            {selectedMeasureId && (
                <MeasureOffcanvas
                    showOffcanvas={!!selectedMeasureId}
                    handleCloseOffcanvas={handleCloseMeasureOffcanvas}
                    measureId={selectedMeasureId}
                    actionAreaId={actionArea.id}
                    unitName={unitName}
                    unitId={actionArea.unit}
                />
            )}
        </div>
    );
};

ActionAreaAccordion.propTypes = {
    actionArea: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        actionarea_title: PropTypes.string,
        status: PropTypes.string,
        unit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        measures: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            })
        ),
    }).isRequired,
    onSaveActionArea: PropTypes.func,
    isReadOnly: PropTypes.bool,
    unitName: PropTypes.string,
    shouldOpenOffcanvas: PropTypes.bool,
    onOffcanvasClose: PropTypes.func,
};

export default ActionAreaAccordion;
